import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageSerialBadge from "../components/PageSerialBadge";
import WinsAutosLogo from "../components/WinsAutosLogo";
import { doc, getDoc } from "firebase/firestore";
import toast, { Toaster } from "react-hot-toast";
import { db } from "../firebase";
import { fetchEmployeePortalIncentive } from "../services/monthlyIncentiveSnapshotService";
import { fetchEmployeeLeaveSnapshot } from "../helpers/employeeLeaveSnapshot";
import { formatPaidLeaveBalance, MONTHLY_PAID_LEAVE_ALLOWANCE } from "../config/employeeLeavePolicy";
import { verifyEmployeePortalLogin } from "../services/employeePortalAuth";
import {
  getEmployeePortalSession,
  setEmployeePortalSession,
  clearEmployeePortalSession,
} from "../services/employeePortalSession";
import "./EmployeePortalPage.css";

const BRANCH_OPTIONS = [
  { value: "Main", label: "Main Branch" },
  { value: "Branch 2", label: "Spareway" },
  { value: "Branch 3", label: "Branch" },
];

function MIcon({ name, className = "" }) {
  return <span className={`material-symbols-outlined ${className}`}>{name}</span>;
}

function fmtMoney(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v));
}

function fmtCompact(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(v));
}

function toDateFromDynamic(v) {
  if (v == null) return null;
  if (typeof v?.toDate === "function") return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v === "number") return new Date(v);
  if (typeof v === "string") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatDojShort(d) {
  if (!d) return "—";
  return d.toLocaleString("en-IN", { month: "short", year: "numeric" });
}

function tenurePartsBetween(startDate, endDate) {
  if (!(startDate instanceof Date) || isNaN(startDate.getTime())) return null;
  if (!(endDate instanceof Date) || isNaN(endDate.getTime())) return null;
  if (endDate < startDate) return { years: 0, months: 0 };

  let years = endDate.getFullYear() - startDate.getFullYear();
  let months = endDate.getMonth() - startDate.getMonth();
  if (endDate.getDate() < startDate.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return { years: 0, months: 0 };
  return { years, months };
}

function formatTenureLabel(doj, asOf = new Date()) {
  const parts = tenurePartsBetween(doj, asOf);
  if (!parts) return "—";
  const { years, months } = parts;
  if (years === 0 && months === 0) return "< 1 month";
  const chunks = [];
  if (years > 0) chunks.push(`${years} yr${years === 1 ? "" : "s"}`);
  if (months > 0) chunks.push(`${months} mo${months === 1 ? "" : "s"}`);
  return chunks.join(" ");
}

function employeeInitial(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] || "?").toUpperCase();
}

const INCENTIVE_CACHE_KEY = "epIncentiveCache_v2";

function incentiveCacheKey(branch, year, monthIndex, employeeId) {
  return `${INCENTIVE_CACHE_KEY}:${branch}:${year}-${monthIndex}:${employeeId}`;
}

function readIncentiveCache(branch, year, monthIndex, employeeId) {
  try {
    const raw = sessionStorage.getItem(incentiveCacheKey(branch, year, monthIndex, employeeId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeIncentiveCache(branch, year, monthIndex, employeeId, snap) {
  try {
    sessionStorage.setItem(incentiveCacheKey(branch, year, monthIndex, employeeId), JSON.stringify(snap));
  } catch {
    /* quota / private mode */
  }
}

/** Mini attendance strip for the month (present / absent / leave / half / empty). */
function buildAttendanceStrip(leaveDetails, slotCount = 12) {
  if (!leaveDetails) return Array(slotCount).fill("empty");
  const present = Math.max(0, Math.round(Number(leaveDetails.presentDays) || 0));
  const absent = Math.max(0, Number(leaveDetails.absentDays) || 0);
  const leave = Math.max(0, Number(leaveDetails.paidLeaveCount) || 0);
  const half = Math.max(0, Number(leaveDetails.halfDayCount) || 0);
  const cells = [];
  for (let i = 0; i < present && cells.length < slotCount; i++) cells.push("present");
  for (let i = 0; i < leave && cells.length < slotCount; i++) cells.push("leave");
  for (let i = 0; i < half && cells.length < slotCount; i++) cells.push("half");
  for (let i = 0; i < absent && cells.length < slotCount; i++) cells.push("absent");
  while (cells.length < slotCount) cells.push("empty");
  return cells.slice(0, slotCount);
}

const ATTENDANCE_CELL_CLASS = {
  present: "ep-attendance-present rounded",
  absent: "ep-attendance-absent rounded",
  leave: "ep-attendance-leave rounded",
  half: "ep-attendance-half rounded",
  empty: "ep-attendance-empty rounded",
};

function LoadingPulse({ className = "h-10 w-24" }) {
  return <div className={`ep-shimmer ${className}`} />;
}

const LEAVE_DETAIL_MODALS = {
  absent: {
    title: "Absent day details",
    icon: "event_busy",
    countLabel: (n) => `${n} absent day(s) this month`,
    emptyText: "No absent days recorded for this month.",
    datesKey: "absentDates",
    countKey: "absentDays",
    dotClass: "ep-attendance-absent",
  },
  half: {
    title: "Half day details",
    icon: "schedule",
    countLabel: (n) => `${n} half day(s) this month`,
    emptyText: "No half days recorded for this month.",
    datesKey: "halfDayDates",
    countKey: "halfDayCount",
    dotClass: "ep-attendance-half",
  },
};

export default function EmployeePortalPage() {
  const [session, setSession] = useState(() => getEmployeePortalSession());
  const [branchFilter, setBranchFilter] = useState(() => session?.branchFilter || "Main");
  const [loginInput, setLoginInput] = useState("");
  const [pin, setPin] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [incentive, setIncentive] = useState(null);
  const [incLoading, setIncLoading] = useState(false);
  const [leaveDetails, setLeaveDetails] = useState(null);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveDetailModal, setLeaveDetailModal] = useState(null);
  const [specialIncentiveModalOpen, setSpecialIncentiveModalOpen] = useState(false);

  const reloadProfile = useCallback(async () => {
    const s = getEmployeePortalSession();
    if (!s?.docId || !s?.empCollectionName) return;
    setProfileLoading(true);
    try {
      const ref = doc(db, s.empCollectionName, s.docId);
      const snap = await getDoc(ref);
      if (snap.exists()) setProfile({ id: snap.id, ...(snap.data() || {}) });
      else setProfile(null);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Could not load profile");
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    const s = getEmployeePortalSession();
    setSession(s);
    if (s?.branchFilter) setBranchFilter(s.branchFilter);
  }, []);

  useEffect(() => {
    if (!session) return;
    reloadProfile();
  }, [session, reloadProfile]);

  const loadIncentive = useCallback(async () => {
    const s = getEmployeePortalSession();
    if (!s?.employeeId) return;

    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const cached = readIncentiveCache(s.branchFilter, year, monthIndex, s.employeeId);
    if (cached) {
      setIncentive(cached);
      setIncLoading(false);
    } else {
      setIncLoading(true);
    }

    try {
      const snap = await fetchEmployeePortalIncentive({
        year,
        monthIndex,
        branchFilter: s.branchFilter,
        employeeId: s.employeeId,
        displayName: s.displayName || "",
      });
      setIncentive(snap);
      writeIncentiveCache(s.branchFilter, year, monthIndex, s.employeeId, snap);
    } catch (e) {
      console.error(e);
      if (!cached) {
        toast.error(e?.message || "Incentive failed to load");
        setIncentive(null);
      }
    } finally {
      setIncLoading(false);
    }
  }, [month]);

  useEffect(() => {
    if (!session) return;
    loadIncentive();
  }, [session, loadIncentive]);

  const loadLeave = useCallback(async () => {
    const s = getEmployeePortalSession();
    if (!s?.employeeId) return;
    setLeaveLoading(true);
    try {
      const snap = await fetchEmployeeLeaveSnapshot(
        s.branchFilter,
        month.getFullYear(),
        month.getMonth(),
        s.employeeId
      );
      setLeaveDetails(snap);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Leave details failed to load");
      setLeaveDetails(null);
    } finally {
      setLeaveLoading(false);
    }
  }, [month]);

  useEffect(() => {
    if (!session) return;
    loadLeave();
  }, [session, loadLeave]);

  useEffect(() => {
    if (!leaveDetailModal && !specialIncentiveModalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setLeaveDetailModal(null);
        setSpecialIncentiveModalOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [leaveDetailModal, specialIncentiveModalOpen]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await verifyEmployeePortalLogin(branchFilter, loginInput, pin);
      if (!res.ok) {
        toast.error(res.error || "Login failed");
        return;
      }
      setEmployeePortalSession({
        docId: res.docId,
        empCollectionName: res.empCollectionName,
        employeeId: res.employeeId,
        displayName: res.displayName,
        branchFilter,
      });
      setSession(getEmployeePortalSession());
      setPin("");
      toast.success(`Welcome, ${res.displayName}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const onLogout = () => {
    clearEmployeePortalSession();
    setSession(null);
    setProfile(null);
    setIncentive(null);
    setLeaveDetails(null);
    toast.success("Signed out");
  };

  const monthLabel = month.toLocaleString("en-IN", { month: "long", year: "numeric" });
  const monthShort = month.toLocaleString("en-IN", { month: "short", year: "numeric" }).toUpperCase();
  const incentiveLeaveLines = (incentive?.myLines || []).filter((L) => Number(L.leave) > 0);
  const attendanceStrip = useMemo(() => buildAttendanceStrip(leaveDetails), [leaveDetails]);
  const specialIncentiveSummary = useMemo(() => {
    const lines = incentive?.specialGroupLines || [];
    if (!lines.length) return null;
    const totalSale = lines.reduce((a, G) => a + (Number(G.saleAmount) || 0), 0);
    const totalIncentive =
      Number(incentive?.specialGroupTotal) ||
      lines.reduce((a, G) => a + (Number(G.incentiveAmount) || 0), 0);
    const ratePct = lines[0]?.ratePct ?? 0;
    return { lines, totalSale, totalIncentive, ratePct };
  }, [incentive?.specialGroupLines, incentive?.specialGroupTotal]);

  const earningsRows = useMemo(() => {
    const brandRows = (incentive?.myLines || []).flatMap((L) => {
      const brand = L.brandId || "—";
      const shareMeta = `${L.pct}% share${Number(L.leave) > 0 ? ` · ${L.leave} leave` : ""}`;
      return [
        {
          key: `${L.brandId}-${L.index}-oe`,
          label: `OE ${brand}`,
          sub: shareMeta,
          sale: L.oeSale,
          incentive: L.myOeIncentive,
          saleClass: "text-[#00355f]",
          incClass: "text-[#00355f]",
          clickable: false,
        },
        {
          key: `${L.brandId}-${L.index}-ord`,
          label: `Ord ${brand}`,
          sub: shareMeta,
          sale: L.ordSale,
          incentive: L.myOrdIncentive,
          saleClass: "text-[#006a6a]",
          incClass: "text-[#006a6a]",
          clickable: false,
        },
      ];
    });
    const specialRow = specialIncentiveSummary
      ? [
          {
            key: "special-incentive",
            label: "Special Incentive",
            sub: `${specialIncentiveSummary.ratePct}% · ${specialIncentiveSummary.lines.length} groups`,
            sale: specialIncentiveSummary.totalSale,
            incentive: specialIncentiveSummary.totalIncentive,
            saleClass: "text-[#00355f]",
            incClass: "text-[#006a6a]",
            clickable: true,
          },
        ]
      : [];
    const pool = incentive?.staffPoolPct;
    const poolRow =
      pool && Number(pool.amount) >= 0
        ? [
            {
              key: "staff-pool-pct",
              label: "Staff pool incentive",
              sub: `${pool.ratePct}% of other staff · base ₹${fmtMoney(pool.othersTotal)}`,
              sale: pool.othersTotal,
              incentive: pool.amount,
              saleClass: "text-[#00355f]",
              incClass: "text-[#006a6a]",
              clickable: false,
            },
          ]
        : [];
    return [...brandRows, ...specialRow, ...poolRow];
  }, [incentive?.myLines, incentive?.staffPoolPct, specialIncentiveSummary]);
  const hasEarningsRows = earningsRows.length > 0;

  if (!session) {
    return (
      <div className="ep-root ep-login-bg flex min-h-screen items-center justify-center px-4 py-8">
        <Toaster position="top-right" />
        <div className="relative w-full max-w-sm ep-fade-in">
          <div className="absolute right-0 top-0 z-10">
            <PageSerialBadge variant="menu" />
          </div>
          <div className="mb-5 text-center">
            <div className="mx-auto mb-3 flex justify-center">
              <WinsAutosLogo size={48} />
            </div>
            <p className="ep-label-caps text-[#8cf3f3]">Staff self-service</p>
            <h1 className="ep-display mt-1.5 text-xl font-bold tracking-tight text-white">Employee Portal</h1>
            <p className="mx-auto mt-2 max-w-xs text-xs leading-relaxed text-white/65">
              Sign in with your employee ID or name and the portal PIN from HR.
            </p>
          </div>
          <form onSubmit={onLogin} className="ep-login-glass rounded-lg p-4 sm:p-5">
            <div className="space-y-3">
              <label className="block">
                <span className="ep-label-caps mb-1.5 block text-white/60">Branch</span>
                <select
                  className="ep-login-input w-full rounded-md px-3 py-2"
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                >
                  {BRANCH_OPTIONS.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="ep-label-caps mb-1.5 block text-white/60">Employee name or ID</span>
                <input
                  className="ep-login-input w-full rounded-md px-3 py-2 placeholder:text-white/40"
                  value={loginInput}
                  onChange={(e) => setLoginInput(e.target.value)}
                  placeholder="Full name or employee code"
                  autoComplete="username"
                  required
                />
              </label>
              <label className="block">
                <span className="ep-label-caps mb-1.5 block text-white/60">Portal PIN</span>
                <input
                  type="password"
                  className="ep-login-input w-full rounded-md px-3 py-2 placeholder:text-white/40"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="PIN from HR"
                  autoComplete="current-password"
                  required
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={loginLoading}
              className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-md bg-[#8cf3f3] py-2.5 text-xs font-semibold text-[#002020] transition hover:bg-[#6fd7d6] disabled:opacity-60"
            >
              {loginLoading ? (
                <>
                  <MIcon name="progress_activity" className="animate-spin text-[18px]" />
                  Signing in…
                </>
              ) : (
                <>
                  <MIcon name="login" className="text-[18px]" />
                  Sign in to portal
                </>
              )}
            </button>
          </form>
          <p className="mt-5 text-center text-[11px] text-white/50">
            <Link to="/login" className="text-[#a0c9ff] underline-offset-4 hover:underline">
              Staff / admin login (inventory)
            </Link>
          </p>
        </div>
      </div>
    );
  }

  const doj = toDateFromDynamic(profile?.DOJ);
  const tenureLabel = formatTenureLabel(doj);
  const dept = String(profile?.department ?? profile?.Department ?? "").trim();
  const initial = employeeInitial(session.displayName);
  const branchLabel = BRANCH_OPTIONS.find((b) => b.value === session.branchFilter)?.label ?? session.branchFilter;
  const presentTotal = leaveDetails?.workingDaysInRange
    ? `${leaveDetails.presentDays ?? 0}/${leaveDetails.workingDaysInRange}`
    : leaveDetails?.presentDays ?? "—";
  const leaveProgressPct =
    leaveDetails != null
      ? Math.min(100, Math.round((leaveDetails.paidLeaveCount / MONTHLY_PAID_LEAVE_ALLOWANCE) * 100))
      : 0;
  const leaveDetailConfig = leaveDetailModal ? LEAVE_DETAIL_MODALS[leaveDetailModal] : null;
  const leaveDetailDates = leaveDetailConfig ? leaveDetails?.[leaveDetailConfig.datesKey] ?? [] : [];
  const leaveDetailCount = leaveDetailConfig
    ? leaveDetails?.[leaveDetailConfig.countKey] ?? leaveDetailDates.length
    : 0;

  return (
    <div className="ep-root ep-body-bg min-h-screen overflow-x-hidden">
      <Toaster position="top-right" />

      {/* Glass top nav */}
      <header className="ep-glass-nav fixed left-0 right-0 top-0 z-50 h-11 px-3 sm:px-4">
        <div className="mx-auto flex h-full max-w-5xl items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <MIcon name="shield_with_heart" className="shrink-0 text-[#00355f] text-[20px]" />
            <h1 className="ep-display truncate text-sm font-semibold tracking-tight text-[#00355f]">
              Employee Portal
            </h1>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              aria-label="Previous month"
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#42474f] transition hover:bg-slate-100 hover:text-[#00355f]"
              onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
            >
              <MIcon name="chevron_left" />
            </button>
            <span className="ep-display hidden min-w-[7rem] text-center text-xs font-semibold text-[#00355f] sm:block">
              {monthLabel}
            </span>
            <span className="ep-display min-w-[3.5rem] text-center text-[10px] font-semibold text-[#00355f] sm:hidden">
              {monthShort}
            </span>
            <button
              type="button"
              aria-label="Next month"
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#42474f] transition hover:bg-slate-100 hover:text-[#00355f]"
              onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
            >
              <MIcon name="chevron_right" />
            </button>
            <div className="mx-0.5 hidden h-5 w-px bg-[#c2c7d1]/50 sm:block" />
            <PageSerialBadge variant="plain" />
            <div className="flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-white/70 py-0.5 pl-2 pr-0.5">
              <div className="hidden text-right sm:block">
                <p className="ep-display max-w-[7rem] truncate text-[11px] font-semibold leading-tight text-[#111c2c]">
                  {session.displayName}
                </p>
                <p className="ep-label-caps text-[#727780]">{branchLabel}</p>
              </div>
              <div className="ep-display flex h-6 w-6 items-center justify-center rounded-full border border-[#00355f]/15 bg-[#d2e4ff] text-[10px] font-bold text-[#00355f]">
                {initial}
              </div>
            </div>
            <button
              type="button"
              onClick={onLogout}
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#ba1a1a] transition hover:bg-red-50 hover:text-[#93000a]"
              title="Sign out"
            >
              <MIcon name="logout" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-3 pb-8 pt-12 sm:px-4">
        <div className="grid grid-cols-12 gap-3">
          {/* Profile column */}
          <div className="col-span-12 lg:col-span-3 ep-fade-in">
            <div className="ep-glass-card space-y-2.5 rounded-lg p-3">
              <div className="flex items-center gap-2.5">
                <div className="relative shrink-0">
                  <div className="ep-display relative flex h-9 w-9 items-center justify-center rounded-full border border-[#00355f]/10 bg-[#d2e4ff] text-xs font-bold text-[#00355f]">
                    {initial}
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border border-white bg-[#22c55e]" />
                </div>
                <div className="min-w-0">
                  <h3 className="ep-display truncate text-sm font-semibold leading-tight text-[#00355f]">
                    {session.displayName}
                  </h3>
                  <p className="truncate text-xs text-[#42474f]">{dept || "Employee"}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-0 rounded-md bg-slate-50/80 px-1 py-1.5 text-center">
                <div>
                  <p className="ep-label-caps mb-0.5 text-[9px] text-[#727780]">Emp ID</p>
                  <p className="ep-mono text-xs font-semibold text-[#111c2c]">{session.employeeId}</p>
                </div>
                <div className="border-x border-[#c2c7d1]/30 px-0.5">
                  <p className="ep-label-caps mb-0.5 text-[9px] text-[#727780]">Joined</p>
                  <p className="ep-mono text-xs font-semibold text-[#111c2c]">
                    {doj ? formatDojShort(doj) : "—"}
                  </p>
                </div>
                <div>
                  <p className="ep-label-caps mb-0.5 text-[9px] text-[#727780]">Experience</p>
                  <p className="ep-mono text-[11px] font-semibold leading-tight text-[#111c2c]">
                    {profileLoading ? "…" : tenureLabel}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-md border border-[#00355f]/8 bg-[#00355f]/5 px-2.5 py-1">
                <span className="ep-label-caps text-[10px] font-bold text-[#00355f]/70">Branch</span>
                <span className="text-xs font-semibold text-[#00355f]">{branchLabel}</span>
              </div>

              {profileLoading ? (
                <LoadingPulse className="h-10 w-full" />
              ) : profile ? (
                <div className="space-y-0.5 text-xs">
                  {[
                    { icon: "call", label: "Phone", value: profile.phone },
                    { icon: "badge", label: "Status", value: profile.Status },
                    {
                      icon: "work",
                      label: "Type",
                      value: profile.EmployementType ?? profile.EmploymentType,
                    },
                  ].map((row) => (
                    <div
                      key={row.label}
                      className="flex items-center justify-between rounded-md px-1.5 py-1 transition hover:bg-slate-50/80"
                    >
                      <span className="flex items-center gap-1.5 text-[#42474f]">
                        <MIcon name={row.icon} className="text-[15px] text-[#727780]" />
                        {row.label}
                      </span>
                      <span className="ep-mono max-w-[55%] truncate text-right font-semibold text-[#00355f]">
                        {row.value ?? "—"}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/* OE & Ordinary metric cards */}
          <div className="col-span-12 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:col-span-9 ep-fade-in" style={{ animationDelay: "60ms" }}>
            <div className="ep-metric-primary flex flex-col justify-between rounded-lg px-3.5 py-3">
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <p className="ep-label-caps font-semibold text-[#d2e4ff]/85">Total OE Sale</p>
                  <MIcon name="analytics" className="text-[20px] text-[#a0c9ff]/50" />
                </div>
                {incLoading ? (
                  <LoadingPulse className="h-7 w-28 bg-white/20" />
                ) : (
                  <h4 className="ep-metric-value text-white">{fmtCompact(incentive?.totalBranchOe ?? 0)}</h4>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between">
                <div className="ep-mono flex items-center gap-1 rounded-full border border-white/15 bg-white/10 px-2 py-0.5 text-[10px]">
                  <MIcon name="trending_up" className="text-[14px] text-[#8cf3f3]" />
                  <span className="font-semibold">{monthShort}</span>
                </div>
                <span className="ep-label-caps opacity-70">Your brands</span>
              </div>
            </div>

            <div className="ep-glass-card flex flex-col justify-between rounded-lg border-l-2 border-l-[#006a6a] px-3.5 py-3 text-[#00355f]">
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <p className="ep-label-caps font-semibold text-[#006a6a]">Total Ordinary Sale</p>
                  <MIcon name="shopping_cart_checkout" className="text-[20px] text-[#006a6a]/25" />
                </div>
                {incLoading ? (
                  <LoadingPulse className="h-7 w-28" />
                ) : (
                  <h4 className="ep-metric-value text-[#00355f]">{fmtCompact(incentive?.totalBranchOrd ?? 0)}</h4>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between">
                <div className="ep-mono flex items-center gap-1 rounded-full border border-[#006a6a]/10 bg-[#006a6a]/8 px-2 py-0.5 text-[10px]">
                  <MIcon name="check_circle" className="text-[14px] text-[#006a6a]" />
                  <span className="font-semibold text-[#006a6a]">Branch net</span>
                </div>
                <span className="ep-label-caps text-[#00355f]/50">{monthShort}</span>
              </div>
            </div>
          </div>

          {/* Leave / Attendance */}
          <div className="col-span-12 lg:col-span-5 ep-fade-in" style={{ animationDelay: "100ms" }}>
            <div className="ep-glass-card h-full rounded-lg p-3.5">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="ep-section-title flex items-center gap-1.5">
                  <MIcon name="event_available" className="text-[#006a6a] text-[16px]" />
                  Leave &amp; Attendance
                </h3>
                <div className="rounded-md border border-slate-200/80 bg-white/70 px-2 py-0.5">
                  {leaveLoading ? (
                    <LoadingPulse className="inline-block h-4 w-10" />
                  ) : (
                    <>
                      <span className="ep-mono text-sm font-bold text-[#00355f]">{presentTotal}</span>
                      <span className="text-[11px] text-[#727780]"> present</span>
                    </>
                  )}
                </div>
              </div>

              <div className="mb-3 grid grid-cols-12 gap-1">
                {attendanceStrip.map((kind, i) => (
                  <div key={i} className={`h-2 ${ATTENDANCE_CELL_CLASS[kind]}`} title={kind} />
                ))}
              </div>

              <div className="space-y-0.5">
                {[
                  {
                    icon: "beach_access",
                    label: "Paid leave used",
                    value: leaveLoading ? null : formatPaidLeaveBalance(leaveDetails?.paidLeaveCount ?? 0),
                  },
                  {
                    icon: "event_busy",
                    label: "Absent days",
                    value: leaveLoading ? null : (leaveDetails?.absentDays ?? "—"),
                  },
                  {
                    icon: "schedule",
                    label: "Half days",
                    value: leaveLoading ? null : (leaveDetails?.halfDayCount ?? 0),
                  },
                ].map((row) => {
                  const modalKey =
                    row.label === "Half days" ? "half" : row.label === "Absent days" ? "absent" : null;
                  const isClickable = Boolean(modalKey) && !leaveLoading;
                  const RowTag = isClickable ? "button" : "div";
                  return (
                    <RowTag
                      key={row.label}
                      type={isClickable ? "button" : undefined}
                      onClick={isClickable ? () => setLeaveDetailModal(modalKey) : undefined}
                      className={`flex w-full items-center justify-between rounded-md px-1.5 py-1 text-xs transition hover:bg-slate-50/80 ${
                        isClickable ? "cursor-pointer text-left" : ""
                      }`}
                    >
                      <span className="flex items-center gap-1.5 text-[#42474f]">
                        <MIcon name={row.icon} className="text-[16px] text-[#727780]" />
                        {row.label}
                        {isClickable ? (
                          <MIcon name="chevron_right" className="text-[14px] text-[#727780]" />
                        ) : null}
                      </span>
                      {row.value == null ? (
                        <LoadingPulse className="h-5 w-10" />
                      ) : (
                        <span className="ep-mono font-bold text-[#00355f]">{row.value}</span>
                      )}
                    </RowTag>
                  );
                })}
              </div>

              <div className="mt-3 rounded-md border border-slate-200/70 bg-slate-50/60 p-2.5">
                <div className="mb-2 flex items-center justify-between">
                  <span className="ep-label-caps font-semibold text-[#42474f]">Paid leave</span>
                  <span className="rounded-full bg-[#006a6a] px-2 py-0.5 text-[9px] font-semibold text-white">
                    {MONTHLY_PAID_LEAVE_ALLOWANCE} / month
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-[#c2c7d1]/25">
                  <div
                    className="h-full rounded-full bg-[#006a6a] transition-all duration-500"
                    style={{ width: `${leaveProgressPct}%` }}
                  />
                </div>
                <p className="mt-1.5 text-[10px] text-[#42474f]/75">
                  {leaveLoading
                    ? "Loading…"
                    : leaveDetails?.paidLeaveRemaining > 0
                      ? `${leaveDetails.paidLeaveRemaining} day(s) remaining this month`
                      : leaveDetails?.paidLeaveOverAllowance > 0
                        ? "Over monthly allowance — contact HR"
                        : `All ${MONTHLY_PAID_LEAVE_ALLOWANCE} paid leave days used`}
                </p>
              </div>

              {leaveDetails?.paidLeaveDates?.length > 0 ? (
                <ul className="mt-2.5 divide-y divide-slate-200/60 rounded-md border border-slate-200/60 bg-white/50 text-xs">
                  {leaveDetails.paidLeaveDates.map((row) => (
                    <li key={row.date} className="flex justify-between px-2 py-1.5">
                      <span className="ep-mono font-medium">{row.dateLabel}</span>
                      <span className="text-[#42474f]">{row.label}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              {!incLoading && incentiveLeaveLines.length > 0 ? (
                <div className="mt-2.5 rounded-md border border-slate-200/60 bg-white/50 p-2">
                  <p className="ep-label-caps mb-1.5 text-[#42474f]">Incentive leave cuts</p>
                  <ul className="space-y-0.5 text-xs">
                    {incentiveLeaveLines.map((L) => (
                      <li key={`${L.brandId}-${L.index}`} className="flex justify-between">
                        <span className="font-medium text-[#00355f]">{L.brandId}</span>
                        <span className="ep-mono text-[#ba1a1a]">−{fmtMoney(L.cut)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>

          {/* Earnings insight */}
          <div className="col-span-12 lg:col-span-7 ep-fade-in" style={{ animationDelay: "140ms" }}>
            <div className="ep-glass-card flex h-full flex-col overflow-hidden rounded-lg">
              <div className="flex items-center justify-between border-b border-slate-200/60 bg-slate-50/50 px-3 py-2">
                <h3 className="ep-section-title flex items-center gap-1.5">
                  <MIcon name="payments" className="text-[#006a6a] text-[16px]" />
                  Earnings Insight
                </h3>
                <span className="ep-label-caps rounded-md border border-slate-200/70 bg-white/80 px-2 py-0.5 text-[#42474f]">
                  {monthShort}
                </span>
              </div>

              <div className="max-h-[220px] flex-1 overflow-y-auto">
                {incLoading ? (
                  <div className="space-y-2 p-3">
                    <LoadingPulse className="h-10 w-full" />
                    <LoadingPulse className="h-10 w-full" />
                  </div>
                ) : hasEarningsRows ? (
                  <ul className="divide-y divide-slate-200/60">
                    {earningsRows.map((row) => {
                      const rowBody = (
                        <>
                          <div className="flex items-center gap-2.5">
                            <div className="flex h-7 w-7 items-center justify-center rounded-md border border-[#00355f]/8 bg-[#00355f]/6 text-[#00355f]">
                              <MIcon
                                name={row.clickable ? "stars" : "verified"}
                                className="text-[16px]"
                              />
                            </div>
                            <div>
                              <p className="ep-display flex items-center gap-1 text-xs font-semibold text-[#00355f]">
                                {row.label}
                                {row.clickable ? (
                                  <MIcon name="chevron_right" className="text-[14px] text-[#727780]" />
                                ) : null}
                              </p>
                              <p className="ep-label-caps tracking-normal text-[#727780]">{row.sub}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`ep-mono text-sm font-bold ${row.saleClass}`}>{fmtMoney(row.sale)}</p>
                            <span className={`text-[9px] font-semibold ${row.incClass}`}>
                              {row.clickable
                                ? `Incentive amount · ₹${fmtMoney(row.incentive)}`
                                : `₹${fmtMoney(row.incentive)} inc.`}
                            </span>
                          </div>
                        </>
                      );
                      return (
                        <li key={row.key}>
                          {row.clickable ? (
                            <button
                              type="button"
                              onClick={() => setSpecialIncentiveModalOpen(true)}
                              className="ep-earnings-row flex w-full items-center justify-between px-3 py-2 text-left transition hover:bg-slate-50/80"
                            >
                              {rowBody}
                            </button>
                          ) : (
                            <div className="ep-earnings-row flex items-center justify-between px-3 py-2">
                              {rowBody}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="p-5 text-center text-xs text-[#42474f]">
                    {incentive?.note || "No incentive rows for your Employee ID this month."}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between border-t border-slate-200/70 bg-slate-50/70 px-3 py-2">
                <div>
                  <span className="ep-label-caps font-semibold text-[#42474f]">Estimated total</span>
                  <p className="text-[10px] text-[#727780]">
                    {incentive?.staffPoolPct
                      ? `${incentive.staffPoolPct.ratePct}% of all other staff`
                      : "Staff by brand · final pay"}
                  </p>
                </div>
                <div className="text-right">
                  {incLoading ? (
                    <LoadingPulse className="ml-auto h-6 w-20" />
                  ) : (
                    <span className="ep-mono text-base font-bold text-[#00355f]">
                      ₹{fmtMoney(incentive?.totalFinal ?? 0)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer className="mt-5 text-center">
          <p className="ep-label-caps text-[#727780]">
            {monthLabel} · {branchLabel} · Secured with portal PIN
          </p>
        </footer>
      </main>

      {specialIncentiveModalOpen && specialIncentiveSummary ? (
        <div
          className="ep-modal-backdrop fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => setSpecialIncentiveModalOpen(false)}
          role="presentation"
        >
          <div
            className="ep-glass-card ep-modal w-full max-w-md rounded-lg p-4 shadow-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ep-special-incentive-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-start justify-between gap-2">
              <div>
                <h2 id="ep-special-incentive-title" className="ep-section-title flex items-center gap-1.5">
                  <MIcon name="stars" className="text-[#00355f] text-[18px]" />
                  Special Incentive
                </h2>
                <p className="mt-0.5 text-[11px] text-[#727780]">
                  {monthLabel} · {specialIncentiveSummary.ratePct}% of branch group sale
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSpecialIncentiveModalOpen(false)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#42474f] transition hover:bg-slate-100 hover:text-[#00355f]"
                aria-label="Close"
              >
                <MIcon name="close" className="text-[18px]" />
              </button>
            </div>

            <ul className="max-h-[min(50vh,320px)] space-y-1 overflow-y-auto rounded-md border border-slate-200/60 bg-white/50 p-2">
              {specialIncentiveSummary.lines.map((G) => (
                <li
                  key={G.groupId}
                  className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-slate-50/80"
                >
                  <div>
                    <p className="font-semibold text-[#00355f]">{G.groupId}</p>
                    <p className="text-[10px] text-[#727780]">{G.ratePct}% incentive</p>
                  </div>
                  <div className="text-right">
                    <p className="ep-mono font-medium text-[#00355f]">{fmtMoney(G.saleAmount)}</p>
                    <p className="ep-mono text-[10px] font-semibold text-[#006a6a]">
                      ₹{fmtMoney(G.incentiveAmount)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-3 flex items-center justify-between rounded-md border border-[#00355f]/10 bg-[#00355f]/5 px-3 py-2 text-xs">
              <span className="font-semibold text-[#00355f]">Total</span>
              <div className="text-right">
                <p className="ep-mono font-bold text-[#00355f]">
                  {fmtMoney(specialIncentiveSummary.totalSale)}
                </p>
                <p className="ep-mono text-[11px] font-bold text-[#006a6a]">
                  ₹{fmtMoney(specialIncentiveSummary.totalIncentive)}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {leaveDetailConfig ? (
        <div
          className="ep-modal-backdrop fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => setLeaveDetailModal(null)}
          role="presentation"
        >
          <div
            className="ep-glass-card ep-modal w-full max-w-sm rounded-lg p-4 shadow-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ep-leave-detail-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-start justify-between gap-2">
              <div>
                <h2 id="ep-leave-detail-title" className="ep-section-title flex items-center gap-1.5">
                  <MIcon name={leaveDetailConfig.icon} className="text-[#00355f] text-[18px]" />
                  {leaveDetailConfig.title}
                </h2>
                <p className="mt-0.5 text-[11px] text-[#727780]">{monthLabel}</p>
              </div>
              <button
                type="button"
                onClick={() => setLeaveDetailModal(null)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#42474f] transition hover:bg-slate-100 hover:text-[#00355f]"
                aria-label="Close"
              >
                <MIcon name="close" />
              </button>
            </div>

            <p className="ep-label-caps mb-2 text-[#42474f]">
              {leaveDetailConfig.countLabel(leaveDetailCount)}
            </p>

            {leaveDetailDates.length ? (
              <ul className="max-h-56 divide-y divide-slate-200/60 overflow-y-auto rounded-md border border-slate-200/60 bg-white/50 text-xs">
                {leaveDetailDates.map((dateLabel) => (
                  <li key={dateLabel} className="flex items-center gap-2 px-2.5 py-2">
                    <span className={`h-2 w-2 shrink-0 rounded-sm ${leaveDetailConfig.dotClass}`} />
                    <span className="ep-mono font-medium text-[#00355f]">{dateLabel}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="rounded-md border border-slate-200/60 bg-slate-50/80 px-3 py-4 text-center text-xs text-[#727780]">
                {leaveDetailConfig.emptyText}
              </p>
            )}

            {leaveDetails?.attendanceError ? (
              <p className="mt-2 text-[11px] text-amber-800">{leaveDetails.attendanceError}</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
